import {
	useCarousel,
	useCarouselGestures,
	useCarouselKeyboard,
} from '#app/hooks/use-carousel.ts'
import { Button } from './ui/button.tsx'
import { Icon } from './ui/icon.tsx'

// Hero Slide Component with 3D effects
interface HeroSlideProps {
	title: string
	subtitle: string
	backgroundImage: string
	decorativeImage: string
	isActive?: boolean
	slideIndex: number
	currentIndex: number
	showActionButton?: boolean
	actionButtonText?: string
	onActionClick?: () => void
}

function HeroSlide({
	title,
	subtitle,
	backgroundImage,
	decorativeImage,
	slideIndex,
	currentIndex,
	showActionButton,
	actionButtonText = 'Cotizar mi seguro',
	onActionClick,
}: HeroSlideProps) {
	// Calculate 3D transform based on position relative to current slide
	const getSlideTransform = () => {
		const diff = slideIndex - currentIndex
		const rotateY = diff * 45 // 45 degrees rotation between slides (menos agresivo)
		const translateZ = diff === 0 ? 0 : -150 // Active slide comes forward
		const translateX = diff * 250 // Horizontal spacing
		const opacity = Math.abs(diff) > 1 ? 0 : diff === 0 ? 1 : 0.5

		return {
			transform: `translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotateY}deg)`,
			opacity,
			zIndex: diff === 0 ? 30 : Math.max(1, 20 - Math.abs(diff)), // Active slide always on top
		}
	}

	const { transform, opacity, zIndex } = getSlideTransform()

	return (
		<div
			className="absolute inset-0 transition-all duration-800 ease-in-out"
			style={{
				transform,
				opacity,
				zIndex,
				transformStyle: 'preserve-3d',
			}}
		>
			{/* Background Image - Optimized */}
			<div className="absolute inset-0 overflow-hidden rounded-[20px]">
				<div
					className="absolute inset-0 bg-cover bg-center"
					style={{
						backgroundImage: `url('/resources/images?src=${encodeURIComponent(backgroundImage)}&w=1200&h=900&format=webp')`,
					}}
				/>
				{/* Dark overlay for better text readability */}
				<div className="absolute inset-0 bg-black/40" />
			</div>

			{/* Decorative Lines Overlay - Optimized */}
			<div
				className="pointer-events-none absolute inset-0 bg-center bg-no-repeat opacity-60"
				style={{
					backgroundImage: `url('/resources/images?src=${encodeURIComponent(decorativeImage)}&w=1200&h=900&format=webp')`,
					backgroundSize: 'cover',
				}}
			/>

			{/* Main Content */}
			<div className="relative z-10 flex h-full items-center justify-center">
				{/* Mobile Layout - siguiendo Figma exacto */}
				<div className="flex w-[300px] flex-col items-start justify-center gap-8 px-4 md:hidden">
					<div className="flex w-full flex-col gap-3 text-white">
						<h1 className="font-parkinsans text-[40px] leading-[56px] font-medium text-white">
							{title}
						</h1>
						<p className="font-inter text-[18px] leading-[35px] font-medium text-white">
							{subtitle}
						</p>
					</div>

					{/* CTA Button - usando design system para mobile */}
					{showActionButton && (
						<Button
							variant="secondary"
							appearance="stroke"
							size="m"
							className="self-start bg-white"
							onClick={onActionClick}
						>
							{actionButtonText}
						</Button>
					)}
				</div>

				{/* Desktop Layout - mantiene implementación original */}
				<div className="hidden w-full max-w-[1200px] flex-col items-start justify-center gap-8 px-6 md:flex">
					<div className="flex flex-col items-start justify-start gap-3 text-white">
						<h1 className="font-parkinsans max-w-[803px] text-4xl leading-tight font-medium text-white md:text-5xl lg:text-[64px] lg:leading-[80px]">
							{title}
						</h1>
						<p className="font-inter max-w-[803px] text-lg leading-relaxed font-medium text-white md:text-xl lg:text-[24px] lg:leading-[32px]">
							{subtitle}
						</p>
					</div>

					{/* CTA Button usando el design system para desktop */}
					{showActionButton && (
						<Button
							variant="secondary"
							appearance="stroke"
							size="l"
							className="bg-white"
							onClick={onActionClick}
						>
							{actionButtonText}
						</Button>
					)}
				</div>
			</div>
		</div>
	)
}

// Hero Carousel Component with 3D effects
interface HeroCarouselProps {
	autoPlay?: boolean
	interval?: number
	showControls?: boolean
	showIndicators?: boolean
	showActionButton?: boolean
	actionButtonText?: string
	onActionClick?: () => void
	onSlideChange?: (index: number) => void
	className?: string
	slides: {
		id: string
		title: string
		subtitle: string
		backgroundImage: string
		decorativeImage: string
	}[]
}

export function HeroCarousel({
	autoPlay = true,
	interval = 5000,
	showControls = true,
	showIndicators = true,
	onSlideChange,
	className,
	slides,
	showActionButton = true,
	actionButtonText = 'Cotizar mi seguro',
	onActionClick,
}: HeroCarouselProps) {
	// Use carousel hook for state management
	const {
		currentIndex,
		isTransitioning,
		isPaused,
		goToNext,
		goToPrevious,
		goToSlide,
		pause,
		play,
		toggle,
	} = useCarousel({
		totalSlides: slides.length,
		autoPlay,
		autoPlayInterval: interval,
		loop: true,
		onSlideChange,
	})

	// Touch/swipe gesture support
	const { handleTouchStart, handleTouchMove, handleTouchEnd } =
		useCarouselGestures({
			onSwipeLeft: goToNext,
			onSwipeRight: goToPrevious,
			swipeThreshold: 50,
			enabled: true,
		})

	// Keyboard navigation support
	const { handleKeyDown } = useCarouselKeyboard({
		onNext: goToNext,
		onPrevious: goToPrevious,
		onToggle: toggle,
		enabled: true,
	})

	return (
		<section className="px-4 py-0">
			<div
				className={`relative h-[900px] w-full overflow-hidden rounded-[20px] bg-black ${className || ''}`}
				onMouseEnter={pause}
				onMouseLeave={play}
				onKeyDown={handleKeyDown}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
				tabIndex={0}
				role="region"
				aria-label="3D Hero Carousel"
				aria-live="polite"
				style={{
					perspective: '1200px',
					transformStyle: 'preserve-3d',
				}}
			>
				{/* 3D Slides Container */}
				<div className="relative size-full">
					{slides.map((slide, index) => (
						<HeroSlide
							key={slide.id}
							title={slide.title}
							subtitle={slide.subtitle}
							backgroundImage={slide.backgroundImage}
							decorativeImage={slide.decorativeImage}
							isActive={index === currentIndex}
							slideIndex={index}
							currentIndex={currentIndex}
							showActionButton={showActionButton}
							actionButtonText={actionButtonText}
							onActionClick={onActionClick}
						/>
					))}
				</div>
				{/* Navigation Controls - hidden on mobile según Figma */}
				{showControls && (
					<>
						{/* Previous Button - Only render on desktop */}
						<div className="hidden md:block">
							<Button
								onClick={goToPrevious}
								disabled={isTransitioning}
								variant="primary"
								appearance="ghost"
								size="m"
								iconOnly
								className="absolute top-1/2 left-6 z-30 h-12 w-12 -translate-y-1/2 rounded-full bg-white/20 text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/30"
								aria-label="Previous slide"
							>
								<Icon name="arrow-left" className="h-5 w-5" />
							</Button>
						</div>

						{/* Next Button - Only render on desktop */}
						<div className="hidden md:block">
							<Button
								onClick={goToNext}
								disabled={isTransitioning}
								variant="primary"
								appearance="ghost"
								size="m"
								iconOnly
								className="absolute top-1/2 right-6 z-30 h-12 w-12 -translate-y-1/2 rounded-full bg-white/20 text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/30"
								aria-label="Next slide"
							>
								<Icon name="arrow-right" className="h-5 w-5" />
							</Button>
						</div>
					</>
				)}
				{/* Indicators - ocultos en mobile según Figma */}
				{showIndicators && (
					<div className="absolute bottom-8 left-1/2 z-30 hidden -translate-x-1/2 gap-3 md:flex">
						{slides.map((_, index) => (
							<Button
								key={index}
								onClick={() => goToSlide(index)}
								variant="secondary"
								appearance="outline"
								size="xs"
								iconOnly
								disabled={isTransitioning}
								className={`min-w-0 rounded-full p-0 transition-all duration-300 ${
									index === currentIndex
										? 'scale-125 opacity-100'
										: 'opacity-50 hover:opacity-70'
								}`}
								aria-label={`Go to slide ${index + 1}`}
							/>
						))}
					</div>
				)}
				{/* Auto-play pause indicator */}
				{autoPlay && isPaused && (
					<div className="absolute top-6 right-6 z-40 rounded-full bg-black/50 px-3 py-1 text-sm text-white backdrop-blur-sm">
						Paused
					</div>
				)}
				{/* Screen Reader Instructions */}
				<div className="sr-only">
					Use arrow keys to navigate slides, spacebar to pause auto-play.
					Currently showing slide {currentIndex + 1} of {slides.length}.
				</div>
			</div>
		</section>
	)
}

export default HeroCarousel
